<template>
  <div class="assistant-window" :class="{ 'assistant-window--expanded': expanded }">
    <header class="assistant-window__header">
      <div class="assistant-window__identity">
        <span class="assistant-window__badge" aria-hidden="true">
          <Icon icon="mdi:creation" />
        </span>
        <div class="assistant-window__titles">
          <h2 class="assistant-window__title">CRM Assistant</h2>
          <p class="assistant-window__status">
            <span
              class="assistant-window__dot"
              :class="{ 'assistant-window__dot--busy': isSending || isThreadWorking }"
            />
            {{ statusText }}
          </p>
        </div>
      </div>

      <div class="assistant-window__tools">
        <button
          v-if="!expanded"
          type="button"
          class="assistant-window__tool"
          :class="{ 'assistant-window__tool--active': showHistory }"
          :aria-pressed="showHistory"
          :title="showHistory ? 'Back to chat' : 'Past chats'"
          :aria-label="showHistory ? 'Back to chat' : 'Past chats'"
          @click="showHistory = !showHistory"
        >
          <Icon :icon="showHistory ? 'mdi:message-text-outline' : 'mdi:history'" />
        </button>
        <button
          type="button"
          class="assistant-window__tool"
          title="New chat"
          aria-label="New chat"
          :disabled="isSending"
          @click="startNew"
        >
          <Icon icon="mdi:square-edit-outline" />
        </button>
        <button
          type="button"
          class="assistant-window__tool"
          :title="expanded ? 'Shrink to panel' : 'Expand'"
          :aria-label="expanded ? 'Shrink to panel' : 'Expand'"
          @click="emit('toggle-expand')"
        >
          <Icon :icon="expanded ? 'mdi:arrow-collapse' : 'mdi:arrow-expand'" />
        </button>
        <button
          type="button"
          class="assistant-window__tool"
          title="Close"
          aria-label="Close assistant"
          @click="emit('close')"
        >
          <Icon icon="mdi:close" />
        </button>
      </div>
    </header>

    <div class="assistant-window__main">
      <AgentConversationList
        v-if="expanded || showHistory"
        class="assistant-window__threads"
        :class="{ 'assistant-window__threads--full': !expanded }"
        mode="assistant"
        :active-id="conversationId"
        :disabled="isSending"
        @select="selectThread"
        @new="startNew"
        @deleted="onDeleted"
      />
      <AssistantChat v-if="expanded || !showHistory" :roomy="expanded" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import { Icon } from "@/components/ui";
import AgentConversationList from "@/components/agent/AgentConversationList.vue";
import AssistantChat from "@/components/agent/assistant/AssistantChat.vue";
import { useAssistantChat } from "@/composables/agent/useAssistantChat";

const props = defineProps<{
  /** Centered modal layout, with past chats beside the conversation. */
  expanded: boolean;
}>();

const emit = defineEmits<{
  close: [];
  "toggle-expand": [];
}>();

const { conversationId, isSending, isThreadWorking, isLoadingThread, open, reset, showHistory } =
  useAssistantChat();

// The docked panel is narrow, so past chats take its place instead of sitting beside it.
// Shared, so the bubble can open straight onto the list when several replies came in.
watch(
  () => props.expanded,
  () => (showHistory.value = false)
);

const statusText = computed(() => {
  if (isSending.value) return "Thinking…";
  if (isThreadWorking.value) return "Still working…";
  if (isLoadingThread.value) return "Loading…";
  return "Drafts wait for your approval";
});

const selectThread = (id: string) => {
  open(id);
  showHistory.value = false;
};

const startNew = () => {
  reset();
  showHistory.value = false;
};

const onDeleted = (id: string) => {
  if (id === conversationId.value) reset();
};
</script>

<style scoped>
.assistant-window {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: #fff;
}

.assistant-window__header {
  position: relative;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 14px 14px 16px;
  overflow: hidden;
  background: linear-gradient(120deg, #6d28d9 0%, #5b4cf0 45%, #3b82f6 100%);
  color: #fff;
}

/* Soft light sweeping across the header. */
.assistant-window__header::before {
  content: "";
  position: absolute;
  inset: -40% auto auto -10%;
  width: 60%;
  height: 180%;
  background: radial-gradient(closest-side, rgb(255 255 255 / 22%), transparent);
  pointer-events: none;
}

.assistant-window__identity {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.assistant-window__badge {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid rgb(255 255 255 / 30%);
  border-radius: 12px;
  background: rgb(255 255 255 / 18%);
  font-size: 20px;
  backdrop-filter: blur(4px);
}

.assistant-window__titles {
  min-width: 0;
}

.assistant-window__title {
  margin: 0;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.assistant-window__status {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 2px 0 0;
  color: rgb(255 255 255 / 82%);
  font-size: 12px;
  white-space: nowrap;
}

.assistant-window__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 0 3px rgb(74 222 128 / 25%);
}

.assistant-window__dot--busy {
  background: #fde68a;
  box-shadow: 0 0 0 3px rgb(253 230 138 / 30%);
  animation: assistant-window-pulse 1.2s ease-in-out infinite;
}

.assistant-window__tools {
  position: relative;
  display: flex;
  flex-shrink: 0;
  gap: 4px;
}

.assistant-window__tool {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: rgb(255 255 255 / 90%);
  font-size: 18px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.assistant-window__tool:hover:not(:disabled),
.assistant-window__tool--active {
  background: rgb(255 255 255 / 18%);
  color: #fff;
}

.assistant-window__tool:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 1px;
}

.assistant-window__tool:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.assistant-window__main {
  display: flex;
  flex: 1;
  min-height: 0;
}

/* The shared conversation list, restyled for the assistant. */
.assistant-window__threads {
  background: #fafafc;
}

.assistant-window__threads--full {
  width: 100%;
  border-right: none;
}

.assistant-window--expanded .assistant-window__header {
  padding: 16px 18px 16px 22px;
}

@keyframes assistant-window-pulse {
  50% {
    opacity: 0.5;
  }
}

@media (prefers-reduced-motion: reduce) {
  .assistant-window__dot--busy {
    animation: none;
  }
}
</style>
